import { Box, Tooltip, Typography, IconButton, CircularProgress } from "@mui/material";
import { isInternalIp } from "./ImportantFunctions";
import LanguageIcon from "@mui/icons-material/Language";
import Flag from "react-world-flags";
import BackupIcon from "@mui/icons-material/Backup";
import PanToolIcon from "@mui/icons-material/PanTool";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { startToast, updateToastError, updateToastSuccess } from "../utils/notification-loading";
import { useState } from "react";

export const AckButton = ({ row, onAck }) => {
    const [loading, setLoading] = useState(false);
    const acked = !!row.acked;
    const handleClick = async (e) => {
        e.stopPropagation();
        if (!row.event_id) {
            updateToastError("This log has no event_id and cannot be acknowledged");
            return;
        }
        setLoading(true);
        await onAck(row, !acked);
        setLoading(false);
    };
    return (
        <Tooltip title={acked ? "Acknowledged — click to undo" : "Mark as acknowledged"} arrow>
            <IconButton size="small" onClick={handleClick} disabled={loading}>
                {loading
                    ? <CircularProgress size={16} />
                    : acked
                        ? <CheckCircleIcon sx={{ fontSize: 20, color: "#4caf50" }} />
                        : <RadioButtonUncheckedIcon sx={{ fontSize: 20, color: "#aaa" }} />
                }
            </IconButton>
        </Tooltip>
    );
};

export const securityColumns = ({
    timezone,
    tipo,
    reseller,
    names,
    onAck,
    onIpClick,
    rareSignatures,
}) => {
    const handleDetailClick = (detail) => {
        startToast();
        if (navigator?.clipboard?.writeText) {
            navigator.clipboard.writeText(detail)
                .then(() => updateToastSuccess("Detail copied to clipboard"))
                .catch(() => updateToastError("Failed to copy detail"));
        } else {
            const textarea = document.createElement("textarea");
            textarea.value = detail;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand("copy");
            document.body.removeChild(textarea);
            updateToastSuccess("Detail copied to clipboard");
        }
    }
    return (
        [
            {
                field: "timestamp",
                headerName: `TIMESTAMP (${timezone ? timezone : "GMT"})`,
                minWidth: 180,
                headerAlign: "left",
                align: "left",
                type: "string",
                filterable: false,
                renderCell: (params) => {
                    if (!params.value) return <span>-</span>;

                    const date = new Date(params.value);
                    const tz = timezone || "GMT";

                    const formatted = new Intl.DateTimeFormat("sv-SE", {
                        timeZone: tz,
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                        //fractionalSecondDigits: 3,
                        hour12: false,
                    }).format(date);

                    // Calcola offset es. "+02:00"
                    // const offsetMinutes = -date.toLocaleString("en", { timeZone: tz, timeZoneName: "shortOffset" })
                    //     .split("GMT")[1]?.replace(":", "") * 60 || 0;

                    // const sign = offsetMinutes >= 0 ? "+" : "-";
                    // const abs = Math.abs(offsetMinutes);
                    // const hh = String(Math.floor(abs / 60)).padStart(2, "0");
                    // const mm = String(abs % 60).padStart(2, "0");
                    // const offset = `${sign}${hh}:${mm}`;

                    return <span>{formatted.replace("T", " ")}</span>;
                },
            },
            {
                field: "signature",
                headerName: "DETAILS",
                flex: 1,
                minWidth: 280,
                headerAlign: "left",
                align: "left",
                type: "string",
                filterable: false,
                renderCell: (params) => (
                    <Box onClick={() => { handleDetailClick(params.value) }}
                        sx={{
                            display: "flex",
                            alignItems: "flex-start", // 🔹 Permette l'espansione verticale
                            justifyContent: "space-between",
                            width: "100%",
                            minHeight: "600", // 🔹 Espande la riga se necessario
                            maxHeight: "auto",
                            gap: "8px",
                            padding: "4px 0", // 🔹 Evita il testo troppo vicino ai bordi
                        }}
                    >
                        <span style={{
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            flexGrow: 1,
                            maxWidth: "75%",
                            cursor: "pointer"
                        }}
                        >
                            {params.value}
                        </span>

                        <Box sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            minWidth: "60px",
                        }}>
                            {rareSignatures?.has(params.row.signature) && (
                                <Tooltip
                                    title={
                                        <Typography sx={{ fontSize: 15 }}>
                                            Rare: one of the 10 least frequent logs in the last 24h (same list as Highlights → Rarest logs)
                                        </Typography>
                                    }
                                    arrow
                                >
                                    <Box sx={{
                                        px: 0.8, py: 0.1, borderRadius: 0.5, cursor: "default",
                                        fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", lineHeight: 1.6,
                                        color: "#c6a6ff", backgroundColor: "rgba(145,113,216,0.16)", border: "1px solid rgba(145,113,216,0.5)",
                                    }}>
                                        RARE
                                    </Box>
                                </Tooltip>
                            )}
                            {params.row.backup && (
                                <Tooltip
                                    title={
                                        <Typography sx={{ fontSize: 15, color: "#61a9ad" }}>
                                            Possible Backup
                                        </Typography>
                                    }
                                    arrow
                                >
                                    <BackupIcon sx={{ fontSize: 18 }} />
                                </Tooltip>
                            )}
                            {(params.row.supp === 1 || params.row.supp === 2) && (
                                <Tooltip
                                    title={
                                        <Typography sx={{ fontSize: 15 }}>
                                            This Log is Suppressed
                                        </Typography>
                                    }
                                    arrow
                                >
                                    <PanToolIcon sx={{ fontSize: 18 }} />
                                </Tooltip>
                            )}

                        </Box>
                    </Box>
                )
            },
            {
                field: "src_ip",
                headerName: "SOURCE",
                minWidth: 180,
                headerAlign: "left",
                filterable: false,
                renderCell: (params) => {
                    const srcHostnames = params?.row?.src_hostnames;
                    const nameToDisplay =
                        names === "dns"
                            ? srcHostnames?.dns_name
                            : names === "netbios"
                                ? srcHostnames?.netbios_name
                                : srcHostnames?.custom_name;
                    if (!params.value) {
                        return <span>-</span>;
                    }
                    // Niente ":" finale quando la porta sorgente non è disponibile
                    // (es. conduit host-a-host: la porta client è effimera, mai
                    // tracciata dalla pipeline di proposito) -- prima diventava
                    // sempre "IP:", anche a porta assente.
                    const srcPortSuffix = params?.row?.src_port ? `:${params.row.src_port}` : "";
                    return (
                        <div
                            onClick={(e) => {
                                e.stopPropagation();
                                onIpClick?.(params.value, e);
                            }}
                            style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}
                        >
                            <span
                                onMouseEnter={(e) => {
                                    e.currentTarget.innerText = `${params.value}${srcPortSuffix}`;
                                    e.currentTarget.style.textDecoration = "underline";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.innerText = `${nameToDisplay || params.value}${srcPortSuffix}`;
                                    e.currentTarget.style.textDecoration = "none";
                                }}
                            >
                                {nameToDisplay || params.value}{srcPortSuffix}
                            </span>
                            {!isInternalIp(params.row.src_ip) && (
                                <Tooltip
                                    title={
                                        <Typography sx={{ fontSize: 15 }}>
                                            {params.row.countryName || params.row.geo?.src?.country_code || "External Connection"}
                                        </Typography>
                                    }
                                    arrow
                                >
                                    <div style={{ display: "flex", alignItems: "center" }}>
                                        {(params.row.countryCode || params.row.geo?.src?.country_code) ? (
                                            <Flag code={params.row.countryCode || params.row.geo?.src?.country_code} style={{ width: 20, height: 13 }} />
                                        ) : (
                                            <LanguageIcon sx={{ fontSize: 18 }} />
                                        )}
                                    </div>
                                </Tooltip>
                            )}
                        </div>
                    );
                },
            },
            {
                field: "src_mac",
                headerName: "SOURCE MAC",
                minWidth: 120,
                headerAlign: "left",
                renderCell: (params) => {
                    let cellValue = "-"; // Valore di default per i null
                    if (params.value !== "" && params.value !== null && params.value !== undefined) {
                        cellValue = params.value.toString(); // Converte in stringa se non è un array
                    }

                    return <span>{cellValue}</span>;
                },
            },
            {
                field: "dest_ip",
                headerName: "DESTINATION",
                minWidth: 180,
                headerAlign: "left",
                filterable: false,
                renderCell: (params) => {
                    const destHostnames = params?.row?.dest_hostnames;
                    const nameToDisplay =
                        names === "dns"
                            ? destHostnames?.dns_name
                            : names === "netbios"
                                ? destHostnames?.netbios_name
                                : destHostnames?.custom_name;
                    if (!params.value) {
                        return <span>-</span>;
                    }
                    // Stesso criterio di SOURCE qui sopra: niente ":" finale se
                    // dest_port manca.
                    const destPortSuffix = params?.row?.dest_port ? `:${params.row.dest_port}` : "";
                    return (
                        <div
                            onClick={(e) => {
                                e.stopPropagation();
                                onIpClick?.(params.value, e);
                            }}
                            style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}
                        >
                            <span
                                onMouseEnter={(e) => {
                                    e.currentTarget.innerText = `${params.value}${destPortSuffix}`;
                                    e.currentTarget.style.textDecoration = "underline";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.innerText = `${nameToDisplay || params.value}${destPortSuffix}`;
                                    e.currentTarget.style.textDecoration = "none";
                                }}
                            >
                                {nameToDisplay || params.value}{destPortSuffix}
                            </span>
                            {!isInternalIp(params.row.dest_ip) && (
                                <Tooltip
                                    title={
                                        <Typography sx={{ fontSize: 15 }}>
                                            {params.row.countryName || params.row.geo?.dest?.country_code || "External Connection"}
                                        </Typography>
                                    }
                                    arrow
                                >
                                    <div style={{ display: "flex", alignItems: "center" }}>
                                        {(params.row.countryCode || params.row.geo?.dest?.country_code) ? (
                                            <Flag code={params.row.countryCode || params.row.geo?.dest?.country_code} style={{ width: 20, height: 13 }} />
                                        ) : (
                                            <LanguageIcon sx={{ fontSize: 18 }} />
                                        )}
                                    </div>
                                </Tooltip>
                            )}
                        </div>
                    );
                },
            },
            {
                field: "dest_mac",
                headerName: "DESTINATION MAC",
                minWidth: 120,
                headerAlign: "left",
                renderCell: (params) => {
                    let cellValue = "-"; // Valore di default per i null
                    if (params.value !== "" && params.value !== null && params.value !== undefined) {
                        cellValue = params.value.toString(); // Converte in stringa se non è un array
                    }

                    return <span>{cellValue}</span>;
                },
            },
            {
                field: "vlan",
                headerName: "VLANs",
                minWidth: 40,
                headerAlign: "left",
                align: "left",
                filterable: false,
                renderCell: (params) => {
                    let cellValue = "-"; // Valore di default per i null

                    if (Array.isArray(params.value)) {
                        cellValue = params.value.join(", "); // Unisce gli elementi dell'array con uno spazio
                    } else if (params.value) {
                        cellValue = params.value.toString(); // Converte in stringa se non è un array
                    }

                    return <span>{cellValue}</span>;
                },
            },
            {
                field: "proto",
                headerName: "PROTOCOL",
                minWidth: 40,
                headerAlign: "left",
                align: "left",
                filterable: false,
                renderCell: (params) => {
                    let cellValue = params.value;
                    if (
                        params.value === "failed" ||
                        params.value === null ||
                        params.value === undefined
                    ) {
                        cellValue = "-";
                    }
                    return <span>{cellValue}</span>;
                },
            },
            {
                field: "app_proto",
                headerName: "APPLICATION PROTOCOL",
                minWidth: 50,
                headerAlign: "left",
                align: "left",
                filterable: false,
                renderCell: (params) => {
                    let cellValue = params.value;
                    if (
                        params.value === "failed" ||
                        params.value === null ||
                        params.value === undefined
                    ) {
                        cellValue = "-";
                    }
                    return <span>{cellValue}</span>;
                },
            },
            // {
            //     field: "category",
            //     headerName: "CLASS OF DETECTION",
            //     minWidth: 200,
            //     headerAlign: "left",
            //     align: "left",
            //     filterable: false,
            //     hidden: true,
            //     visible: false,
            // },

            ...((tipo === 0 || tipo === 1) && !reseller ? [
                {
                    field: "response",
                    headerName: "RESPONSE",
                    minWidth: 200,
                    headerAlign: "left",
                    align: "left",
                    filterable: false,
                },
            ] : []),
            ...(onAck ? [
                {
                    field: "ack",
                    headerName: "ACK",
                    width: 64,
                    headerAlign: "center",
                    align: "center",
                    sortable: false,
                    filterable: false,
                    disableColumnMenu: true,
                    renderCell: (params) => <AckButton row={params.row} onAck={onAck} />,
                },
            ] : []),
        ]
    )
}
